import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye, Zap, ChevronRight } from 'lucide-react';
import { usePageTitle } from '@hooks/usePageTitle';
import { useCopyScoring } from '../hooks/useCopyScoring';
import { LIVING_AD_EXERCISES } from '../data/copywritingExercises';
import CopyEditor from '../components/CopyEditor';
import CopyScoreDisplay from '../components/CopyScoreDisplay';
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  ResponsiveContainer,
} from 'recharts';

const ACCENT = '#7c3aed';

const LivingAd = () => {
  usePageTitle('The Living Ad | Castle Atlas');
  const { exerciseId } = useParams();
  const navigate = useNavigate();
  const { scoreExercise, loading, error, clearError } = useCopyScoring();

  const [isDark, setIsDark] = useState(() =>
    typeof window !== 'undefined' && document.documentElement.classList.contains('dark')
  );
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.innerWidth < 640
  );

  const [currentIdx, setCurrentIdx] = useState(0);
  const [headline, setHeadline] = useState('');
  const [body, setBody] = useState('');
  const [cta, setCta] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [score, setScore] = useState(null);

  useEffect(() => {
    const obs = new MutationObserver(() =>
      setIsDark(document.documentElement.classList.contains('dark'))
    );
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (exerciseId) {
      const idx = LIVING_AD_EXERCISES.findIndex(e => e.id === exerciseId);
      if (idx >= 0) setCurrentIdx(idx);
    }
  }, [exerciseId]);

  const exercise = LIVING_AD_EXERCISES[currentIdx];
  const isRevision = exercise?.mode === 'revision';

  const handleSubmit = useCallback(async () => {
    if (submitted || !headline.trim()) return;
    setSubmitted(true);

    const fullCopy = `HEADLINE: ${headline}\n\nBODY: ${body}\n\nCTA: ${cta}`;

    try {
      const res = await scoreExercise({
        exerciseId: exercise.id,
        moduleId: 'living-ad',
        mode: exercise.mode,
        userText: fullCopy,
      });
      setResult(res);
      setScore(res.scores?.overall ?? 0);
    } catch (err) {
      // unlock so the student can retry; the hook holds the message shown under the button
      console.error(err);
      setSubmitted(false);
    }
  }, [submitted, headline, body, cta, exercise, scoreExercise]);

  const nextExercise = () => {
    if (currentIdx < LIVING_AD_EXERCISES.length - 1) {
      setCurrentIdx(prev => prev + 1);
      setHeadline(''); setBody(''); setCta('');
      setSubmitted(false); setResult(null); setScore(null); clearError();
      navigate(`/skills/copywriting/living-ad/${LIVING_AD_EXERCISES[currentIdx + 1].id}`, { replace: true });
    }
  };

  const c = {
    pageBg: isDark ? '#0e0d0c' : '#faf9f7',
    card: isDark ? '#1c1917' : '#ffffff',
    border: isDark ? '#2d2825' : '#e8e4df',
    head: isDark ? '#fafaf9' : '#1c1917',
    muted: isDark ? '#78716c' : '#a8a29e',
    sub: isDark ? '#57534e' : '#b4aca6',
    previewBg: isDark ? '#100b1e' : '#faf5ff',
    previewBorder: isDark ? '#4c3580' : '#c4b5fd',
    draftBg: isDark ? 'rgba(239,68,68,0.06)' : 'rgba(239,68,68,0.04)',
    draftBorder: isDark ? 'rgba(239,68,68,0.35)' : 'rgba(239,68,68,0.3)',
  };

  // Radar built dynamically from whatever axes the score comes back with
  const radarData = result?.scores
    ? Object.entries(result.scores)
        .filter(([k]) => k !== 'overall')
        .map(([k, v]) => ({
          dim: k.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim(),
          value: v || 0,
          full: 100,
        }))
    : [];

  const AxisPill = ({ label, muted }) => (
    <span style={{
      display: 'inline-block', padding: '3px 10px', borderRadius: 99,
      background: muted ? (isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)') : 'rgba(124,58,237,0.10)',
      color: muted ? c.muted : ACCENT, fontSize: 10, fontWeight: 700,
      textTransform: 'uppercase', letterSpacing: '0.06em',
    }}>{label}</span>
  );

  if (!exercise) return null;

  return (
    <div style={{ maxWidth: '100%', paddingBottom: 80, background: c.pageBg, minHeight: '100vh' }}>
    <div className="rail-clearance">
      <style>{`
        @keyframes fadeUp { from{opacity:0;transform:translateY(10px);}to{opacity:1;transform:translateY(0);} }
        .anim-fade { animation: fadeUp 0.3s ease-out both; }
      `}</style>

      {/* Top bar */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 50,
        background: isDark ? 'rgba(14,13,12,0.85)' : 'rgba(250,249,247,0.85)',
        backdropFilter: 'blur(12px)',
        borderBottom: `1px solid ${c.border}`,
        padding: '12px 20px',
        display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <button onClick={() => navigate('/skills/copywriting')} style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: 6, borderRadius: 8,
        }}>
          <ArrowLeft size={18} color={c.head} />
        </button>
        <Eye size={16} color={ACCENT} />
        <span style={{ fontSize: 13, fontWeight: 800, color: c.head, flex: 1 }}>The Living Ad</span>
        <span style={{ fontSize: 11, fontWeight: 700, color: c.muted }}>
          {currentIdx + 1} / {LIVING_AD_EXERCISES.length}
        </span>
      </div>

      <div style={{ maxWidth: 960, margin: '0 auto', padding: isMobile ? '16px 16px 40px' : '24px 24px 60px' }}>
        {/* Brief Card */}
        <div className="anim-fade" style={{
          background: c.card, border: `1px solid ${c.border}`,
          borderRadius: 20, padding: '18px 20px', marginBottom: 20,
        }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            {exercise.platform && <AxisPill label={exercise.platform} muted />}
            {exercise.objective && <AxisPill label={exercise.objective} muted />}
            <AxisPill label={exercise.primaryAxis} />
          </div>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: c.head, marginBottom: 10 }}>{exercise.title}</h2>
          <p style={{ fontSize: 13, color: c.sub, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
            {exercise.briefText}
          </p>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: 20,
        }}>
          {/* Editor Panel */}
          <div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <CopyEditor
                key={`${exercise.id}:headline`}
                draftKey={`${exercise.id}:headline`}
                value={headline}
                onChange={setHeadline}
                placeholder="Attention-grabbing headline..."
                label={isRevision ? 'Revised hook' : 'Headline'}
                maxLength={80}
                rows={2}
                isDark={isDark}
                autoFocus
                disabled={submitted}
              />
              <CopyEditor
                key={`${exercise.id}:body`}
                draftKey={`${exercise.id}:body`}
                value={body}
                onChange={setBody}
                placeholder="Body copy that sells the benefit..."
                label="Body"
                rows={5}
                isDark={isDark}
                disabled={submitted}
              />
              <CopyEditor
                key={`${exercise.id}:cta`}
                draftKey={`${exercise.id}:cta`}
                value={cta}
                onChange={setCta}
                placeholder="Call to action..."
                label="CTA"
                maxLength={40}
                rows={1}
                isDark={isDark}
                disabled={submitted}
              />
            </div>

            {!submitted && (
              <button
                onClick={handleSubmit}
                disabled={!headline.trim() || loading}
                style={{
                  width: '100%', marginTop: 16, padding: '14px 24px', borderRadius: 12,
                  background: (!headline.trim() || loading) ? (isDark ? '#2d2825' : '#e8e4df') : ACCENT,
                  color: (!headline.trim() || loading) ? c.muted : '#fff',
                  fontSize: 14, fontWeight: 700, border: 'none', cursor: (!headline.trim() || loading) ? 'not-allowed' : 'pointer',
                  boxShadow: (!headline.trim() || loading) ? 'none' : `0 4px 20px -4px rgba(124,58,237,0.45)`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                }}
              >
                <Zap size={15} />
                {loading ? 'Scoring...' : isRevision ? 'Submit Fix' : 'Submit Ad'}
              </button>
            )}
            {error && !submitted && (
              <p role="alert" style={{ marginTop: 10, fontSize: 12, fontWeight: 600, color: '#ef4444', lineHeight: 1.5 }}>
                {error} Your answer is still here; try again.
              </p>
            )}
          </div>

          {/* Live Preview Panel */}
          <div>
            <div style={{
              background: c.previewBg,
              border: `1.5px solid ${c.previewBorder}`,
              borderRadius: 20,
              padding: '20px 20px 24px',
              minHeight: 280,
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <Eye size={13} color={ACCENT} />
                <span style={{ fontSize: 10, fontWeight: 700, color: ACCENT, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  Live Preview{exercise.platform ? ` — ${exercise.platform}` : ''}
                </span>
              </div>

              {/* Mock Ad Card */}
              <div style={{
                background: isDark ? 'linear-gradient(145deg,#100b1e 0%,#160e28 60%,#1c1232 100%)' : 'linear-gradient(145deg,#faf5ff 0%,#ede9fe 60%,#ddd6fe 100%)',
                borderRadius: 16,
                padding: '24px 20px',
                border: `1px solid ${isDark ? '#4c3580' : '#c4b5fd'}`,
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
                minHeight: 200,
              }}>
                <div style={{
                  width: '100%', height: 120,
                  borderRadius: 12,
                  background: isDark ? 'rgba(124,58,237,0.15)' : 'rgba(124,58,237,0.08)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <span style={{ fontSize: 11, color: isDark ? '#c4b5fd' : '#7c3aed', fontWeight: 600 }}>
                    {exercise.title}
                  </span>
                </div>

                <h3 style={{
                  fontSize: 16, fontWeight: 800,
                  color: isDark ? '#c4b5fd' : '#4c1d95',
                  lineHeight: 1.3,
                  minHeight: 22,
                }}>
                  {headline || <span style={{ color: isDark ? '#4c3580' : '#ddd6fe' }}>Your headline appears here...</span>}
                </h3>

                <p style={{
                  fontSize: 13, lineHeight: 1.6,
                  color: isDark ? '#a78bfa' : '#6d28d9',
                  minHeight: 40,
                }}>
                  {body || <span style={{ color: isDark ? '#4c3580' : '#ddd6fe' }}>Your body copy appears here...</span>}
                </p>

                <div style={{
                  display: 'inline-flex',
                  padding: '10px 20px',
                  borderRadius: 10,
                  background: ACCENT,
                  color: '#fff',
                  fontSize: 13,
                  fontWeight: 700,
                  width: 'fit-content',
                  marginTop: 'auto',
                }}>
                  {cta || 'Your CTA'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Results */}
        {submitted && result && (
          <div className="anim-fade" style={{ marginTop: 24 }}>
            <CopyScoreDisplay
              score={score}
              isNew
              isDark={isDark}
              extraScores={result.scores}
              feedback={result.feedback}
            />

            {radarData.length > 0 && (
              <div style={{
                marginTop: 20, background: c.card, border: `1px solid ${c.border}`,
                borderRadius: 20, padding: '20px',
              }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: c.head, marginBottom: 8 }}>Score breakdown</div>
                <ResponsiveContainer width="100%" height={isMobile ? 220 : 280}>
                  <RadarChart data={radarData} margin={{ top: 20, right: 30, bottom: 20, left: 30 }}>
                    <PolarGrid stroke={isDark ? '#2d2825' : '#e8e4df'} />
                    <PolarAngleAxis dataKey="dim" tick={{ fill: isDark ? '#78716c' : '#a8a29e', fontSize: 11, fontWeight: 600 }} />
                    <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fill: c.muted, fontSize: 9 }} />
                    <Radar name="Score" dataKey="value" stroke={ACCENT} fill={ACCENT} fillOpacity={0.2} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            )}

            {currentIdx < LIVING_AD_EXERCISES.length - 1 && (
              <button
                onClick={nextExercise}
                style={{
                  width: '100%', marginTop: 16, padding: '14px 24px', borderRadius: 12,
                  background: ACCENT, color: '#fff',
                  fontSize: 14, fontWeight: 700, border: 'none', cursor: 'pointer',
                  boxShadow: `0 4px 20px -4px rgba(124,58,237,0.45)`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                }}
              >
                Next Exercise <ChevronRight size={15} />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
    </div>
  );
};

export default LivingAd;
